'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../_components/_ui/background-beams'
import Image from 'next/image'

export default function StatusPage() {
    const [services, setServices] = useState([
        { name: 'AWS', status: 'loading', uptime: '0%', latency: '0ms' },
        { name: 'Cloudflare', status: 'loading', uptime: '0%', latency: '0ms' },
        { name: 'Supabase', status: 'loading', uptime: '0%', latency: '0ms' }
    ])
    const [lastUpdated, setLastUpdated] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchServiceStatus()
        const interval = setInterval(fetchServiceStatus, 60000) // Update every minute
        return () => clearInterval(interval)
    }, [])

    const fetchServiceStatus = async () => {
        try {
            // In a real implementation, you would make API calls to each service's status endpoints
            // This is a simulation for demonstration purposes
            
            // Simulate API calls with random data
            const updatedServices = services.map(service => {
                const isUp = Math.random() > 0.1 // 90% chance of being up
                const latency = Math.floor(Math.random() * 200) + 20 // Random latency between 20-220ms
                const uptime = (99 + Math.random()).toFixed(2) // Random uptime between 99-100%
                
                return {
                    ...service,
                    status: isUp ? 'operational' : (Math.random() > 0.5 ? 'degraded' : 'down'),
                    uptime: `${uptime}%`,
                    latency: `${latency}ms`
                }
            })
            
            setServices(updatedServices)
            setLastUpdated(new Date())
            setLoading(false)
        } catch (error) {
            console.error('Error fetching service status:', error)
            setLoading(false)
        }
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'operational':
                return 'bg-green-500'
            case 'degraded':
                return 'bg-yellow-500'
            case 'down':
                return 'bg-red-500'
            default:
                return 'bg-gray-500'
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case 'operational':
                return 'Operational'
            case 'degraded':
                return 'Degraded Performance'
            case 'down':
                return 'Service Disruption'
            default:
                return 'Loading...'
        }
    }

    const ServiceCard = ({ service }) => {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-neutral-900/50 backdrop-blur-sm rounded-lg overflow-hidden border border-neutral-800 p-6"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)} mr-3`}></div>
                        <h3 className="text-xl font-bold text-white">{service.name}</h3>
                    </div>
                    <span className="text-sm text-gray-400">{getStatusText(service.status)}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                        <p className="text-gray-400 text-sm">Uptime</p>
                        <p className="text-white font-medium">{service.uptime}</p>
                    </div>
                    <div>
                        <p className="text-gray-400 text-sm">Latency</p>
                        <p className="text-white font-medium">{service.latency}</p>
                    </div>
                </div>
            </motion.div>
        )
    }

    const StatsSection = () => {
        const allOperational = services.every(service => service.status === 'operational')
        
        const stats = [
            { label: "Services Monitored", value: services.length },
            { label: "Overall Status", value: allOperational ? "All Systems Go" : "Issues Detected" },
            { label: "Last Checked", value: lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : "Checking..." }
        ]
        
        return (
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="bg-neutral-900/50 py-12 my-16 rounded-lg border border-neutral-800"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-3 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <p className="text-xl font-bold text-white">{stat.value}</p>
                                <p className="text-gray-400 mt-2">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>
        )
    }

    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="container mx-auto px-4 py-16 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-3xl mx-auto"
                >
                    <div className="flex items-center justify-center mb-8">
                        <Image 
                            src="/images/focus.svg" 
                            alt="Status Icon" 
                            width={48} 
                            height={48} 
                            className="filter-white mr-4"
                        />
                        <h1 className="text-4xl font-bold text-white text-center">
                            System Status
                        </h1>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                        </div>
                    ) : (
                        <>
                            <StatsSection />
                            
                            <div className="space-y-6 mt-8">
                                {services.map((service, index) => (
                                    <ServiceCard key={index} service={service} />
                                ))}
                            </div>

                            <div className="mt-12 text-center">
                                <button
                                    onClick={fetchServiceStatus}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-md transition-colors"
                                >
                                    Refresh Status
                                </button>
                                <p className="text-gray-400 mt-4 text-sm">
                                    Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleString() : 'Checking...'}
                                </p>
                            </div>
                        </>
                    )}

                    <div className="mt-12 text-center">
                        <p className="text-gray-400">
                            Having issues? Please{' '}
                            <a
                                href="/support/contact"
                                className="text-indigo-400 hover:text-indigo-300"
                            >
                                contact us
                            </a>
                            .
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}
